'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRecentlyViewedStore } from '@/stores/recently-viewed';
import { formatPrice } from '@/lib/price';

const PLACEHOLDER = '/placeholder-product.svg';

interface Props {
  /** Hide a specific id (e.g. on a PDP — don't show the page you're on). */
  excludeId?: number;
  /** Heading text override. */
  title?: string;
  /** Max items to render. */
  limit?: number;
}

/**
 * Horizontal scrollable strip of recently viewed products.
 * Reads from localStorage-backed Zustand store, so SSR yields nothing and the
 * section appears after hydration.
 */
export function RecentlyViewedSection({
  excludeId,
  title = 'Senast besökta produkter',
  limit = 12,
}: Props) {
  const allItems = useRecentlyViewedStore((s) => s.items);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;
  const items = allItems.filter((i) => i.id !== excludeId).slice(0, limit);
  if (items.length === 0) return null;

  return (
    <section className="my-10 sm:my-12">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="h-5 w-5 text-gray-500" />
          {title}
        </h2>
        <span className="text-sm text-muted-foreground hidden sm:inline">
          {items.length} {items.length === 1 ? 'produkt' : 'produkter'}
        </span>
      </div>

      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory">
        {items.map((item) => (
          <Link
            key={item.id}
            href={`/produkt/${item.slug || item.id}`}
            className="shrink-0 w-36 sm:w-44 snap-start border rounded-xl p-3 hover:border-primary/40 hover:shadow-md transition-all bg-white"
          >
            <div className="aspect-square mb-2 relative bg-gray-50 rounded-lg overflow-hidden">
              <Image
                src={item.image || PLACEHOLDER}
                alt={item.name}
                fill
                sizes="(max-width: 640px) 144px, 176px"
                className="object-contain p-2"
                referrerPolicy="no-referrer"
              />
            </div>
            {item.manufacturer_name && (
              <p className="text-[11px] text-gray-500 truncate">{item.manufacturer_name}</p>
            )}
            <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 leading-tight">
              {item.name}
            </p>
            <p className="text-sm font-bold mt-1.5 text-gray-900">{formatPrice(item.price)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
